import Cookies from 'js-cookie';
import { callApi, getIsPerPersonActivity } from '../../utils';
import { setAvailableEntertainments, setBookedEntertainments } from './bookedEvents';
import { receiveBookedEntertainments } from './bookedEntertainments';
import { setOpenVenuesAcrossTheBreak } from './dineAround';
import {
  REQUEST_AVAILABLE_ENTERTAINMENTS,
  RECEIVE_AVAILABLE_ENTERTAINMENTS,
  SET_AVAILABLE_ENTERTAINMENTS_FAILURE,
  CLEAR_AVAILABLE_ENTERTAINMENTS_ERROR_MESSAGE,
} from '../constants/availableEntertainments';
import { getIsDineAroundAllowed } from '../../dineAround/dineAroundHelper';

const requestAvailableEntertainments = () => ({
  type: REQUEST_AVAILABLE_ENTERTAINMENTS,
});

const receiveAvailableEntertainments = (
  entertainments,
  arrivalTime,
  latestBookingDateEndTime,
  departureTime,
  latestActivityBookingDate,
  isDaBooking,
  dineAroundBreakfastCategory,
  dineAroundDinnerCategory,
  swimmingFreeFlowStartDate,
) => ({
  type: RECEIVE_AVAILABLE_ENTERTAINMENTS,
  entertainments,
  arrivalTime,
  latestBookingDateEndTime,
  departureTime,
  latestActivityBookingDate,
  isDaBooking,
  dineAroundBreakfastCategory,
  dineAroundDinnerCategory,
  swimmingFreeFlowStartDate,
});

const setAvailableEntertainmentsFailure = error => ({
  type: SET_AVAILABLE_ENTERTAINMENTS_FAILURE,
  error,
});

export const clearAvailableEntertainmentsError = () => ({
  type: CLEAR_AVAILABLE_ENTERTAINMENTS_ERROR_MESSAGE,
});

const extendBookedItems = (config, { entertainments: bookedEntertainments }, availableItems, entertainmentCategoryId) => {
  if (!bookedEntertainments || bookedEntertainments.length === 0 || !availableItems || availableItems.length === 0) {
    return [];
  }
  return bookedEntertainments.map((bookedEntertainment) => {
    const { TimeSlotId: bookedTimeSlotId } = bookedEntertainment;
    let newBookedEntertainment = { ...bookedEntertainment, isBMEItem: false };
    availableItems.forEach((availableItem) => {
      const {
        Locations: availableLocations = [],
        CategoryId: availableItemCategoryId,
        Duration: availableItemDurationId,
      } = availableItem;
      availableLocations.forEach(({ TimeSlots: availableTimeSlots = [] }) => {
        availableTimeSlots.forEach(({ Id: availableId }) => {
          if (availableId === bookedTimeSlotId) {
            newBookedEntertainment = {
              ...bookedEntertainment,
              CategoryId: availableItemCategoryId,
              Duration: availableItemDurationId,
              isBMEItem: availableItemCategoryId.toString() === entertainmentCategoryId.toString(),
              isPerPersonActivity: bookedEntertainment && getIsPerPersonActivity(config, bookedEntertainment.ActivityId),
            };
          }
        });
      });
    });
    return newBookedEntertainment;
  });
};

const getAvailableEntertainmentsByCategory = (
  allEntertainments,
  isDaBooking = false,
  dineAroundBreakfastCategory,
  dineAroundDinnerCategory,
  entertainmentCategory,
  isDineAroundAllowed = false,
) => allEntertainments.filter(({ CategoryId: categoryId }) => {
  if (isDineAroundAllowed && isDaBooking && dineAroundBreakfastCategory && dineAroundDinnerCategory) {
    return categoryId === entertainmentCategory
      || categoryId === dineAroundBreakfastCategory
      || categoryId === dineAroundDinnerCategory;
  }
  return categoryId === entertainmentCategory;
});

export const getAvailableEntertainments = siteCode => async (dispatch, getState) => {
  dispatch(requestAvailableEntertainments());
  const arrivalDate = Cookies.get('_arrivalDate');
  const departureDate = Cookies.get('_departureDate');
  const { config, bookedEntertainments, bookingSearch } = getState();
  const { isDaBooking, seasonSuffix = '' } = bookingSearch;
  const {
    REACT_APP_BOOKING_PREFIX: bookingPrefix,
    REACT_APP_BOOKING_SUBSCRIPTION_KEY: subscriptionKey,
    REACT_APP_LATEST_BOOKING_DATE_ENDTIME: latestBookingDateEndTime,
    REACT_APP_DEPARTURE_TIME: departureTime,
    [`REACT_APP_LATEST_ACTIVITY_BOOKING_DATE${seasonSuffix}`]: latestActivityBookingDate,
    REACT_APP_ENTERTAINMENT_CATEGORY: entertainmentCategory,
    REACT_APP_DINE_AROUND_BREAKFAST_CATEGORY,
    REACT_APP_DINE_AROUND_DINNER_CATEGORY,
    REACT_APP_SWIMMING_FREEFLOW_STARTDATE: swimmingFreeFlowStartDate,
    REACT_APP_ARRIVAL_TIME: arrivalTime,
    REACT_APP_API_TIMEOUT_MS: timeout,
  } = config;
  const dineAroundBreakfastCategory = parseInt(REACT_APP_DINE_AROUND_BREAKFAST_CATEGORY, 10);
  const dineAroundDinnerCategory = parseInt(REACT_APP_DINE_AROUND_DINNER_CATEGORY, 10);
  try {
    const { data = {} } = await callApi(
      {
        method: 'get',
        url: `/common/activity?parkCode=${siteCode.toUpperCase()}&startDate=${arrivalDate}&endDate=${departureDate}&pageSize=999`,
      },
      bookingPrefix,
      subscriptionKey,
      timeout,
    );
    const { isAllowed: isDineAroundAllowed } = getIsDineAroundAllowed(config, arrivalDate, seasonSuffix);
    const availableEntertainments = getAvailableEntertainmentsByCategory(
      data.Data,
      isDaBooking,
      dineAroundBreakfastCategory,
      dineAroundDinnerCategory,
      parseInt(entertainmentCategory, 10),
      isDineAroundAllowed,
    );
    const extendedBookedItemsData = extendBookedItems(config, bookedEntertainments, availableEntertainments, entertainmentCategory);
    const mappedBookedItemsData = extendedBookedItemsData.map(activity => (
      activity.CategoryId === dineAroundBreakfastCategory || activity.CategoryId === dineAroundDinnerCategory
        ? { ...activity, isDineAround: true }
        : { ...activity }
    ));
    dispatch(receiveBookedEntertainments(mappedBookedItemsData));
    if (mappedBookedItemsData.length > 0) {
      dispatch(setBookedEntertainments(mappedBookedItemsData, latestActivityBookingDate));
    }
    dispatch(receiveAvailableEntertainments(
      availableEntertainments,
      arrivalTime,
      latestBookingDateEndTime,
      departureTime,
      latestActivityBookingDate,
      isDaBooking,
      dineAroundBreakfastCategory,
      dineAroundDinnerCategory,
      swimmingFreeFlowStartDate,
    ));
    dispatch(setAvailableEntertainments( // booked items
      availableEntertainments,
      arrivalTime,
      latestBookingDateEndTime,
      departureTime,
      latestActivityBookingDate,
      isDaBooking,
      dineAroundBreakfastCategory,
      dineAroundDinnerCategory,
    ));
    dispatch(setOpenVenuesAcrossTheBreak(
      arrivalDate,
      departureDate,
      availableEntertainments,
      dineAroundBreakfastCategory,
      dineAroundDinnerCategory,
    ));
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error(error); // eslint-disable-line
    dispatch(setAvailableEntertainmentsFailure(error));
  }
};
